/**
 * layoutManager.js — Layout switching (1×1, 1×2, 2×2, MPR), view modes, viewport maximize, cine
 * Extracted from uiManager.js (декомпозиция God Object)
 *
 * @module layoutManager
 */

import { getRenderingEngine, getViewportIds, setupViewports, switchViewMode, loadStack } from '../core/viewport.js';
import { LAYOUTS, MPR_LAYOUTS, layoutMode } from '../core/viewportState.js';
import { planViewModeVolume } from '../core/volumeNeed.js';   // [10.0.4] нужен ли проекции объём
import { activateDataTools } from '../tools/toolManager.js';
import { showToast } from '../utils/helpers.js';
import { emit } from '../core/eventBus.js';
import { log } from '../utils/logger.js';
import { enforceTouchAction } from './mobileStability.js';
import { isCompareActive } from '../core/compareState.js';   // [10.7.0] во время сравнения подсказки о пустых окнах не нужны

// [FIX] AbortController для очистки всех addEventListener при destroy
let _ac = new AbortController();


// ── Helpers ──
const $ = (id) => document.getElementById(id);
const $$ = (sel) => document.querySelectorAll(sel);
const on = (id, evt, fn) => { const el = $(id); if (el) el.addEventListener(evt, fn, { signal: _ac.signal }); };
const onAll = (sel, evt, fn) => $$(sel).forEach(el => el.addEventListener(evt, fn), { signal: _ac.signal });

/** @type {Object} Shared context from uiManager */
let _ctx = null;

/** @type {HTMLElement|null} Currently maximized viewport element */
let _maximizedVpEl = null;

// ══════════════════════════════════════════════════════════════
// PUBLIC API
// ══════════════════════════════════════════════════════════════

/**
 * Initialize layout manager.
 * @param {Object} ctx - Shared context from uiManager
 * @param {function():string} ctx.getCurrentLayout
 * @param {function(string)} ctx.setCurrentLayout
 * @param {function():string} ctx.getCurrentMode
 * @param {function(string)} ctx.setCurrentMode
 * @param {function():string} ctx.getCurrentViewportId
 * @param {function(string)} ctx.setCurrentViewportId
 * @param {function():string} ctx.getCurrentSeries
 * @param {function():string[]} ctx.getCurrentImageIds
 * @param {function():string} ctx.getCurrentVolumeId
 * @param {function(string):Promise} ctx.loadSeriesIntoViewport
 */
export function initLayoutManager(ctx) {
  _ctx = ctx;
  bindLayoutButtons();
  bindViewModes();
  bindCineBar();
}

/** Человеческие названия раскладок для сообщений. */
const LAYOUT_LABELS = Object.freeze({
  '1x1': 'одно окно', '1x2': 'два окна', '2x2': 'четыре окна',
  'mpr': 'MPR — четыре проекции', 'mpr6': 'MPR 6 — четыре проекции и два объёмных',
});

/** Подсказка про сравнение серий — один раз на раскладку за сеанс. */
const _comparisonHintShown = new Set();

/**
 * Change layout and reload current series.
 * Called from layout buttons and hotkeys.
 * @param {string} layout - '1x1'|'1x2'|'2x2'|'mpr'|'mpr6'
 */

// [10.4.2] Запрет на то, что без видеокарты не построится.
//
// В 10.4.0 программа честно писала «объём отключён» — и не запрещала его.
// На Lenovo B570 она всё равно ушла в MPR и показала четыре чёрных окна:
// надпись была, а запрета не было. Теперь запрет настоящий и стоит на входе
// в раскладку и в режим — там, куда ведут и кнопки, и горячие клавиши, и
// восстановление сеанса.
function _объёмРазрешён() {
  const m = (typeof window !== 'undefined') ? window.__renderMode : null;
  return !m || m.volume !== false;
}
let _сказалиПроОбъём = false;
let _сказалиПроМедленность = false;
function _предупредимПроМедленность() {
  const m = (typeof window !== 'undefined') ? window.__renderMode : null;
  if (!m || m.volumeSlow !== true || _сказалиПроМедленность) return;
  _сказалиПроМедленность = true;
  showToast('Реформаты на этой машине считает процессор: окна будут чёрными десятки секунд, это не поломка. Если ждать некогда — вернитесь в одиночное окно.', 'warning', 9000);
}
function _откажем(что) {
  if (!_сказалиПроОбъём) {
    _сказалиПроОбъём = true;
    const m = (typeof window !== 'undefined') ? window.__renderMode : null;
    showToast(`${что} не строится: ${m?.title || 'нет аппаратного WebGL2'}. Снимки листаются, окно яркости и измерения работают.`, 'warning', 6000);
  }
  log.warn(`[Режим] ${что} отклонено — объём в этом режиме недоступен`);
}
export async function changeLayout(layout) {
  try {
    if (layout === 'mpr' || layout === 'mpr6') {
      if (!_объёмРазрешён()) {
        // Объём построить НЕЧЕМ — отказываем.
        _откажем('Реформаты MPR');
        if (_ctx.getCurrentLayout?.() === '1x1') return;
        layout = '1x1';
      } else {
        // [10.4.4] Объём построить можно, но долго. Не запрещаем — предупреждаем:
        // ждать полминуты ради реформата или нет, решает врач.
        _предупредимПроМедленность();
      }
    }
    // [11.1.7] Смена раскладки СНИМАЕТ стартовую заставку.
    //
    // Заставка лежит поверх контейнера окон (absolute, inset 0, z-index 10) и
    // до сих пор снималась только по факту появления снимков. Пока она
    // пропадала навсегда после первой загрузки, наткнуться было негде. С
    // 11.0.8 «Очистить исследования» возвращает её — и любая раскладка,
    // выбранная на пустой программе, рисуется ПОД ней: врач видит заставку и
    // считает, что кнопка не сработала. Ровно это и случилось со «Сравнением».
    //
    // Правило общее, а не про сравнение: врач переключил раскладку — он
    // распорядился экраном, и заставке там больше не место.
    import('../ui/premiumFeatures.js')
      .then((м) => м.снятьЗаставку?.())
      .catch((e) => log.debug('[Раскладка] заставка:', e.message));

    _ctx.setCurrentLayout(layout);
    // [10.0.2] mpr6 — тоже режим реформатов.
    //
    // Здесь стояло `layout === 'mpr' ? 'mpr' : '2d'`, и шесть окон получали
    // режим '2d'. Дальше loadSeriesIntoViewport проверяет `_currentMode !== '2d'`
    // и при '2d' уходит в ветку одиночной стопки: том не строился вовсе, пять
    // окон из шести оставались чёрными. Раскладка выглядела бы сломанной,
    // хотя всё остальное — типы окон, привязка тома, пресеты — сделано верно.
    _ctx.setCurrentMode(layoutMode(layout));

    const vpIds = await setupViewports('viewport-container', layout);
    // setupViewports уже вызывает initTools + activateDataTools → StackScroll
    // НЕ вызываем initTools повторно — это сбрасывало tool state
    _ctx.setCurrentViewportId(vpIds[0]);
    enforceTouchAction();
  
    // [FIX #9] Гарантируем StackScroll + синхронизируем toolbar
    activateDataTools();
    _syncToolbarButtons('StackScroll');
  
    $$('[data-layout]').forEach(b => b.classList.toggle('active', b.dataset.layout === layout));
    // [10.19.19] Видимость бегунка листания — по правилу, после каждой смены
    // раскладки. Без этого он оставался на экране в объёмной проекции.
    try { (await import('./globalSliceSlider.js')).обновитьВидимостьБегунка(); } catch (e) { /* модуль не загружен */ }
    $$('[data-mode]').forEach(b => b.classList.remove('active'));
    $$('.mtop-btn[data-mtop-layout]').forEach(b => b.classList.toggle('active', b.dataset.mtopLayout === layout));
    $$('.mtop-btn[data-mtop-mode]').forEach(b => b.classList.remove('active'));
  
    const currentSeries = _ctx.getCurrentSeries();
    if (currentSeries) _ctx.loadSeriesIntoViewport(currentSeries);

    // [10.0.2] «Зачем 1×2 и 2×2, если данные всё равно только в первом окне?»
    //
    // Данные там ровно потому, что второе окно ждёт ВТОРУЮ серию: надо нажать
    // на пустое окно (оно станет активным), а потом выбрать серию в списке —
    // она откроется именно в нём. Это единственный способ сравнить две серии
    // рядом, и нигде об этом не было сказано ни слова. Раскладка выглядела
    // сломанной, хотя работала как задумано.
    //
    // Говорим один раз на раскладку за сеанс: врач, который уже понял, не
    // должен читать это при каждом переключении.
    // [10.7.0] Во время сравнения двух исследований эта подсказка вредна:
    // окна не пустые, выбирать в них ничего не надо, и совет «нажмите на
    // пустое окно» только сбивает. Увидел на снимке экрана — подсказка
    // всплыла поверх уже готового сравнения.
    const panels = LAYOUTS[layout]?.viewports?.length || 1;
    if (isCompareActive()) {
      showToast(`Раскладка: ${LAYOUT_LABELS[layout] || layout}`, 'info', 1200);
    } else if (!MPR_LAYOUTS.includes(layout) && panels > 1 && !_comparisonHintShown.has(layout)) {
      _comparisonHintShown.add(layout);
      showToast('Нажмите на пустое окно, затем выберите серию — она откроется в нём', 'info', 5000);
    } else {
      showToast(`Раскладка: ${LAYOUT_LABELS[layout] || layout}`, 'info', 1200);
    }
  } catch (e) { log.warn(`[layoutManager] changeLayout failed:`, e.message);  }
}

/**
 * Activate single-viewport view mode.
 * @param {string} mode - '2d'|'axial'|'sagittal'|'coronal'|'3d'
 */
export async function activateViewMode(mode) {
  // Осевые/сагиттальные/корональные проекции и 3D строятся из тома — без него
  // это те же чёрные окна.
  if (mode !== '2d' && !_объёмРазрешён()) { _откажем(mode === '3d' ? 'Объёмный рендер' : 'Проекция ' + mode); mode = '2d'; }
  else if (mode !== '2d') _предупредимПроМедленность();

  try {
    _ctx.setCurrentMode(mode);
    _ctx.setCurrentLayout('1x1');
  
    $$('[data-mode]').forEach(b => b.classList.toggle('active', b.dataset.mode === mode));
    $$('[data-layout]').forEach(b => b.classList.remove('active'));
    try { (await import('./globalSliceSlider.js')).обновитьВидимостьБегунка(); } catch (e) { /* модуль не загружен */ }
    $$('.mtop-btn[data-mtop-mode]').forEach(b => b.classList.toggle('active', b.dataset.mtopMode === mode));
    $$('.mtop-btn[data-mtop-layout]').forEach(b => b.classList.remove('active'));
  
    const imageIds = _ctx.getCurrentImageIds();

    // [10.0.4] Проекции, которым нужен ОБЪЁМ, а не стопка.
    //
    // На телефоне программа открывается в режиме '2d': грузится нативная
    // серия, объём не строится, и идентификатор тома остаётся пустым. Дальше
    // нажатие «Аксиальная» уходило в switchViewMode с volumeId = null, ветка
    // построения не выполнялась вовсе — и человек видел чёрное окно без
    // единого сообщения. Стоило нажать MPR, и всё появлялось: та ветка том
    // как раз создаёт.
    //
    // Теперь идентификатор выводится из текущей серии, и объём строится по
    // требованию — с полосой загрузки, чтобы ожидание было видно.
    const need = planViewModeVolume({
      mode,
      currentVolumeId: _ctx.getCurrentVolumeId(),
      seriesUID: _ctx.getCurrentSeries(),
    });
    const { needsVolume } = need;
    const volumeId = need.volumeId;
    if (need.derived) {
      _ctx.setCurrentVolumeId?.(volumeId);
      log.info(`[Режим] ${mode}: ${need.reason}`);
    }

    const onProgress = needsVolume
      ? (pct, detail) => { try { _ctx.updateLoadingOverlay?.(pct, 'Построение объёма', detail); } catch (e) { /* */ } }
      : null;
    if (needsVolume && volumeId) {
      try { _ctx.showLoadingOverlay?.('Построение объёма', `${imageIds?.length || 0} срезов`, 0); } catch (e) { /* */ }
    }

    let newVpId;
    try {
      newVpId = await switchViewMode('viewport-container', mode, imageIds, volumeId, { onProgress });
    } finally {
      if (needsVolume && volumeId) { try { _ctx.hideLoadingOverlay?.(); } catch (e) { /* */ } }
    }
    _ctx.setCurrentViewportId(newVpId);
  
    // Validate — ensure viewport exists
    try {
      const re = getRenderingEngine();
      if (re && !re.getViewport(newVpId)) {
        const vpIds = getViewportIds();
        if (vpIds.length) _ctx.setCurrentViewportId(vpIds[0]);
      }
    } catch (e) { log.debug('[LayoutManager]', e.message); }
  
    // switchViewMode уже вызывает initTools + activateDataTools внутри
    // НЕ вызываем initTools повторно
    enforceTouchAction();
  
    const currentSeries = _ctx.getCurrentSeries();
    if (currentSeries && imageIds?.length) {
      if (mode === '2d') {
        loadStack(_ctx.getCurrentViewportId(), imageIds).catch(e =>
          log.warn('[ViewMode] stack reload:', e)
        );
      }
      // [FIX #9] Немедленно, не через setTimeout
      activateDataTools();
      _syncToolbarButtons('StackScroll');
    }
  
    const labels = {
      '2d': '2D Stack', 'axial': 'Axial', 'sagittal': 'Sagittal',
      'coronal': 'Coronal', '3d': '3D Volume',
    };
    // [10.3.2] Название режима на экран не выводим: человек только что сам его
    // переключил и видит результат в окне, а подпись проекции стоит на снимке.
    log.debug(`[ViewMode] ${labels[mode] || mode}`);
  } catch (e) { log.warn(`[layoutManager] activateViewMode failed:`, e.message);  }
}

/**
 * [10.0.13] «3D пресет» — показать ШЕСТОЕ окно MPR 6 во весь экран.
 *
 * Три версии подряд я делал это по-своему: отдельное одиночное окно в режиме
 * '3d', в которое сам ставил пресет и камеру. Каждая версия чем-то
 * отличалась от рабочей, и на МР вместо головы выходил полупрозрачный блок.
 * Диагнозы у меня не сходились, и продолжать угадывать нечестно.
 *
 * Поэтому своего пути больше нет вовсе. Кнопка делает ровно то, что вы
 * сказали: включает раскладку MPR 6 и раскрывает её шестое окно —
 * «3D · Пресет» — на весь экран. Это тот самый вьюпорт, который у вас
 * показывает лицо; он строится тем же кодом, что и всегда, и разойтись с
 * собой не может по построению.
 *
 * Повторное нажатие сворачивает обратно к шести окнам.
 */
export async function showPreset3DFullscreen() {
  try {
    const vpEl = () => document.getElementById('viewport-view-3d');

    // Уже развёрнуто — свернуть. Кнопка работает как переключатель.
    const cur = vpEl();
    if (cur?.classList.contains('maximized')) { toggleMaximizeViewport(cur); return; }

    if (_ctx.getCurrentLayout?.() !== 'mpr6') {
      await changeLayout('mpr6');
    }

    // Окна раскладки появляются не мгновенно: ждём именно шестое, а не
    // фиксированную паузу — на телефоне с большой серией её всегда не хватает.
    const el = await _waitForElement('viewport-view-3d', 8000);
    if (!el) { showToast('Объёмное окно ещё готовится — попробуйте ещё раз', 'warning', 2500); return; }
    if (!el.classList.contains('maximized')) toggleMaximizeViewport(el);
  } catch (e) {
    log.warn('[3D пресет]', e.message);
  }
}

/** Дождаться появления элемента по идентификатору. */
function _waitForElement(id, timeoutMs = 8000) {
  return new Promise((resolve) => {
    const found = document.getElementById(id);
    if (found) { resolve(found); return; }
    const started = Date.now();
    const tick = () => {
      const el = document.getElementById(id);
      if (el) { resolve(el); return; }
      if (Date.now() - started > timeoutMs) { resolve(null); return; }
      setTimeout(tick, 120);
    };
    tick();
  });
}

/**
 * Setup default viewport on app startup.
 */
export async function setupDefaultViewport() {
  // [10.4.6] Стартовая раскладка тоже спрашивает про режим.
  //
  // В журнале с живой машины это видно прямо: changeLayout я прикрыл ещё в
  // 10.4.2, а старт шёл мимо него — и получал
  // «VolumeViewports cannot be used whilst CPU Fallback Rendering is enabled».
  // Окна не создавались вовсе, отсюда и чернота. Запрет должен стоять на всех
  // входах, иначе он не запрет, а украшение.
  if (!_объёмРазрешён()) {
    log.warn('[Режим] стартовая раскладка — одиночное окно: объём в этом режиме недоступен');
    try { _ctx.setCurrentLayout('1x1'); _ctx.setCurrentMode('2d'); } catch (e) { /* */ }
  }

  try {
    // [6.7.1] Мобильный старт: одно окно — нативная 2D-стопка первичных срезов.
    // Остальные проекции (MPR, Axial/Sagittal/Coronal, 3D) — по кнопкам верхней панели.
    if (window.innerWidth <= 768) {
      await activateViewMode('2d');
      return;
    }
    const vpIds = await setupViewports('viewport-container', 'mpr');
    // setupViewports уже вызывает initTools + activateDataTools
    _ctx.setCurrentViewportId(vpIds[0]);
    activateDataTools();
    _syncToolbarButtons('StackScroll');
    $$('[data-layout]').forEach(b => b.classList.toggle('active', b.dataset.layout === 'mpr'));
    try { (await import('./globalSliceSlider.js')).обновитьВидимостьБегунка(); } catch (e) { /* модуль не загружен */ }
    $$('[data-mode]').forEach(b => b.classList.remove('active'));
  } catch (e) { log.warn(`[layoutManager] setupDefaultViewport failed:`, e.message);  }
}

/**
 * Toggle maximize/restore of a viewport element (desktop dblclick / mobile double-tap).
 * @param {HTMLElement} vpEl - Viewport container element
 */
export function toggleMaximizeViewport(vpEl) {
  if (_maximizedVpEl && _maximizedVpEl !== vpEl) {
    _maximizedVpEl.classList.remove('maximized');
    _maximizedVpEl.style.cssText = '';
    _maximizedVpEl.querySelectorAll('.vp-orientation-label, .vp-zoom-spread').forEach(el => el.style.removeProperty('display'));
    _maximizedVpEl = null;
  }

  vpEl.classList.toggle('maximized');
  if (vpEl.classList.contains('maximized')) {
    vpEl.style.cssText = 'position:absolute;inset:0;z-index:25;touch-action:none;';
    _maximizedVpEl = vpEl;
    _showMobileRestoreBtn();
  } else {
    vpEl.style.cssText = '';
    vpEl.querySelectorAll('.vp-orientation-label, .vp-zoom-spread').forEach(el => el.style.removeProperty('display'));
    _maximizedVpEl = null;
    _hideMobileRestoreBtn();
  }

  const re = getRenderingEngine();
  if (re) {
    requestAnimationFrame(() => {
      re.resize(true, true);
      if (_maximizedVpEl) {
        _maximizedVpEl.querySelectorAll('.vp-orientation-label, .vp-zoom-spread').forEach(el => el.style.display = 'none');
        setTimeout(() => {
          if (_maximizedVpEl) _maximizedVpEl.querySelectorAll('.vp-orientation-label, .vp-zoom-spread').forEach(el => el.style.display = 'none');
        }, 200);
      }
    });
  }
}

/**
 * Show MPR viewport in fullscreen (called from mobile buttons).
 * @param {string} vpId - 'axial'|'sagittal'|'coronal'|'view-3d'
 */
export function mobileMPRFocus(vpId) {
  const vpEl = document.getElementById(`viewport-${vpId}`);
  if (!vpEl) return;

  if (_maximizedVpEl && _maximizedVpEl !== vpEl) {
    _maximizedVpEl.classList.remove('maximized');
    _maximizedVpEl.style.cssText = '';
    _maximizedVpEl.querySelectorAll('.vp-orientation-label, .vp-zoom-spread').forEach(el => el.style.removeProperty('display'));
  }

  vpEl.classList.add('maximized');
  vpEl.style.cssText = 'position:absolute;inset:0;z-index:25;touch-action:none;';
  _maximizedVpEl = vpEl;
  _ctx.setCurrentViewportId(vpId);
  _showMobileRestoreBtn();

  const re = getRenderingEngine();
  if (re) {
    requestAnimationFrame(() => {
      re.resize(true, true);
      if (_maximizedVpEl) {
        _maximizedVpEl.querySelectorAll('.vp-orientation-label, .vp-zoom-spread').forEach(el => el.style.display = 'none');
        setTimeout(() => {
          if (_maximizedVpEl) _maximizedVpEl.querySelectorAll('.vp-orientation-label, .vp-zoom-spread').forEach(el => el.style.display = 'none');
        }, 200);
      }
    });
  }
}

/**
 * Restore MPR grid (remove maximization).
 */
export function mobileMPRRestore() {
  if (_maximizedVpEl) {
    _maximizedVpEl.classList.remove('maximized');
    _maximizedVpEl.style.cssText = '';
    _maximizedVpEl.querySelectorAll('.vp-orientation-label, .vp-zoom-spread').forEach(el => el.style.display = '');
    _maximizedVpEl = null;
  }
  _hideMobileRestoreBtn();
  const re = getRenderingEngine();
  if (re) requestAnimationFrame(() => re.resize(true, true));
}

// ══════════════════════════════════════════════════════════════
// PRIVATE
// ══════════════════════════════════════════════════════════════

/**
 * [FIX #9] Синхронизирует CSS-класс active на кнопках toolbar с текущим инструментом.
 * Гарантирует что визуально подсвечена правильная кнопка (StackScroll по умолчанию).
 */
function _syncToolbarButtons(toolName) {
  $$('.tool-btn').forEach(b => b.classList.toggle('active', b.dataset.tool === toolName));
  $$('.m-tool-btn').forEach(b => b.classList.toggle('active', b.dataset.tool === toolName));
  const label = $('active-tool-label');
  if (label) label.textContent = toolName;
}

function bindLayoutButtons() {
  ['1x1', '1x2', '2x2', 'mpr', 'mpr6'].forEach(layout => {
    on(`btn-layout-${layout}`, 'click', () => changeLayout(layout));
  });
}

function bindViewModes() {
  onAll('.tb-btn[data-mode]', 'click', (e) => {
    const mode = e.currentTarget.dataset.mode;
    activateViewMode(mode);
  });
}

function bindCineBar() {
  on('cine-play', 'click', () => {
    var api = window.MikhmelViewer;
    if (!api) return;
    var state = api.getCineState ? api.getCineState() : null;
    var btn = $('cine-play');
    if (state && state.playing) {
      api.stopCine();
      if (btn) btn.textContent = '▶';
    } else {
      var fpsEl = $('cine-fps');
      var fps = fpsEl ? parseInt(fpsEl.value) || 12 : 12;
      api.startCine(null, { fps: fps, direction: 'forward', loop: true });
      if (btn) btn.textContent = '⏸';
    }
  });
  on('cine-close', 'click', () => { const bar = $('cine-bar'); if (bar) bar.style.display = 'none'; });
  on('cine-first', 'click', () => { const s = $('cine-slider'); if (s) { s.value = 0; _updateCineFrame(); } });
  on('cine-last', 'click', () => { const s = $('cine-slider'); if (s) { s.value = s.max; _updateCineFrame(); } });
  on('cine-prev', 'click', () => { const s = $('cine-slider'); if (s) { s.value = Math.max(0, parseInt(s.value) - 1); _updateCineFrame(); } });
  on('cine-next', 'click', () => { const s = $('cine-slider'); if (s) { s.value = Math.min(parseInt(s.max), parseInt(s.value) + 1); _updateCineFrame(); } });
  on('cine-slider', 'input', _updateCineFrame);
}

function _updateCineFrame() {
  const s = $('cine-slider'), label = $('cine-frame-num');
  if (s && label) label.textContent = `${s.value}/${s.max}`;
}

function _showMobileRestoreBtn() {
  let btn = document.getElementById('mobile-mpr-restore-btn');
  if (!btn) {
    btn = document.createElement('button');
    btn.id = 'mobile-mpr-restore-btn';
    btn.innerHTML = '⊞ Все панели';
    btn.style.cssText = `
      position: fixed;
      top: 8px;
      left: 50%;
      transform: translateX(-50%);
      z-index: 9999;
      background: rgba(15,23,42,0.92);
      color: #60a5fa;
      border: 1px solid #334155;
      border-radius: 20px;
      padding: 6px 16px;
      font-size: 13px;
      font-weight: 500;
      cursor: pointer;
      backdrop-filter: blur(8px);
      box-shadow: 0 2px 12px rgba(0,0,0,0.5);
      touch-action: manipulation;
    `;
    btn.addEventListener('click', mobileMPRRestore, { signal: _ac.signal });
    document.body.appendChild(btn);
  }
  btn.style.display = 'block';
}

function _hideMobileRestoreBtn() {
  const btn = document.getElementById('mobile-mpr-restore-btn');
  if (btn) btn.style.display = 'none';
}

// ══════════════════════════════════════════════════════════════
// ENTERPRISE FEATURES (v6) — Custom layouts, presets, comparison
// ══════════════════════════════════════════════════════════════

const LAYOUT_LS_KEY = 'mikhmel_layout_presets';

/**
 * Save current layout as a named preset
 * @param {string} name
 */
export function saveLayoutPreset(name) {
  const presets = _getLayoutPresets();
  presets[name] = {
    layout: _ctx?.getCurrentLayout?.(),
    mode: _ctx?.getCurrentMode?.(),
    viewport: _ctx?.getCurrentViewportId?.(),
    timestamp: Date.now(),
  };
  try { localStorage.setItem(LAYOUT_LS_KEY, JSON.stringify(presets)); } catch { /* non-critical */ }
  showToast(`💾 Раскладка "${name}" сохранён`, 'success', 1200);
}

/**
 * Load a saved layout preset
 * @param {string} name
 */
export async function loadLayoutPreset(name) {
  try {
    const presets = _getLayoutPresets();
    const p = presets[name];
    if (!p) { showToast(`❌ Пресет "${name}" не найден`, 'error'); return; }
    if (p.layout) await changeLayout(p.layout);
    if (p.mode && p.mode !== p.layout) await activateViewMode(p.mode);
    showToast(`📐 Раскладка "${name}" загружен`, 'info', 1200);
  } catch (e) { log.warn(`[layoutManager] loadLayoutPreset failed:`, e.message);  }
}

/**
 * List all saved layout presets
 * @returns {Array<{name:string, layout:string, mode:string}>}
 */
export function listLayoutPresets() {
  const presets = _getLayoutPresets();
  return Object.entries(presets).map(([name, p]) => ({ name, layout: p.layout, mode: p.mode }));
}

/**
 * Delete a layout preset
 * @param {string} name
 */
export function deleteLayoutPreset(name) {
  const presets = _getLayoutPresets();
  delete presets[name];
  try { localStorage.setItem(LAYOUT_LS_KEY, JSON.stringify(presets)); } catch { /* non-critical */ }
}

function _getLayoutPresets() {
  try { return JSON.parse(localStorage.getItem(LAYOUT_LS_KEY) || '{}'); } catch { return {}; }
}

/**
 * Cycle through layouts: 1x1 → 1x2 → 2x2 → mpr → 1x1...
 */
export function cycleLayout() {
  const order = ['1x1', '1x2', '2x2', 'mpr'];
  const current = _ctx?.getCurrentLayout?.() || 'mpr';
  const idx = order.indexOf(current);
  const next = order[(idx + 1) % order.length];
  changeLayout(next);
}

/**
 * Quick comparison: split into 1x2 with same series in both viewports
 */
export async function enterComparisonMode() {
  try {
    await changeLayout('1x2');
    showToast('⚖️ Режим сравнения — загрузите 2 серии', 'info', 3000);
  } catch (e) { log.warn(`[layoutManager] enterComparisonMode failed:`, e.message);  }
}

/**
 * Get full layout state for diagnostics
 */
export function getLayoutState() {
  return {
    currentLayout: _ctx?.getCurrentLayout?.(), currentMode: _ctx?.getCurrentMode?.(),
    currentViewportId: _ctx?.getCurrentViewportId?.(), isMaximized: !!_maximizedVpEl,
    maximizedId: _maximizedVpEl?.id || null, viewportIds: getViewportIds(),
    savedPresets: listLayoutPresets().length,
  };
}
export function isViewportMaximized() { return !!_maximizedVpEl; }
export function restoreMaximized() { if (_maximizedVpEl) mobileMPRRestore(); }

// [FIX] Cleanup all event listeners
export function destroyLayoutManager() {
  _ac.abort();
  _ac = new AbortController();
}
